import { z } from 'zod';
/**
 * Knowledge doc section 6. Mappings from Wazuh levels, disk thresholds and
 * CrowdSec decision types all land on this scale.
 */
export const Severity = z.enum(['info', 'low', 'medium', 'high', 'critical']);
/**
 * ISO 8601, with an offset permitted.
 *
 * Everything on the wire and in storage is UTC: an offset is accepted only because
 * it names the same instant, and `timestamptz` normalizes it on the way in. Local
 * time exists in exactly one place, the dashboard, which converts for display from
 * a timezone setting. No agent, helper or API code reasons about local time.
 */
export const IsoTime = z.iso.datetime({ offset: true });
export const ServerId = z
    .string()
    .min(1)
    .max(64)
    .regex(/^[a-z0-9][a-z0-9-]*$/, 'lowercase letters, digits and hyphens; must not start with a hyphen');
/** A server named for a person: its id to link by, its name to read. */
export const ServerRef = z.object({ id: ServerId, name: z.string().max(255) });
export const Labels = z.record(z.string().max(64), z.string().max(256));
/** zod 4 split `z.string().ip()` into separate IPv4 and IPv6 formats. */
export const IpAddress = z.union([z.ipv4(), z.ipv6()]);
/**
 * zod 4 validates CIDR notation properly, so the shape-only regex and the
 * `ipaddr.js` refinement suggested in the contracts reference are not needed.
 */
export const Cidr = z.union([z.cidrv4(), z.cidrv6()]);
/**
 * The widest range an allowlist may hold: a /8 of IPv4, a /16 of IPv6.
 *
 * An allowlist entry makes its addresses unbannable twice over — the API refuses
 * the ban (section 12) and CrowdSec itself ignores what its scenarios see from them
 * (P5.4's `set_allowlist`). `0.0.0.0/0` therefore reads like a setting and is in
 * fact CrowdSec switched off for every address, which is a thing to do by turning
 * the module off where somebody can see it, not by a list entry.
 */
export const ALLOWLIST_MIN_PREFIX = { ipv4: 8, ipv6: 16 };
export const AllowlistRange = Cidr.refine((range) => {
    const prefix = Number(range.slice(range.lastIndexOf('/') + 1));
    return prefix >= (range.includes(':') ? ALLOWLIST_MIN_PREFIX.ipv6 : ALLOWLIST_MIN_PREFIX.ipv4);
}, {
    message: `must be no wider than /${String(ALLOWLIST_MIN_PREFIX.ipv4)} for IPv4 or /${String(ALLOWLIST_MIN_PREFIX.ipv6)} for IPv6`,
});
/**
 * The scale in order, least to most serious.
 *
 * `z.enum` keeps `options` in declaration order, but depending on that would make
 * the ordering a property of how the enum happens to be written. Naming it here is
 * what lets "high and above" be a comparison rather than a set of enum members
 * somebody has to remember to extend.
 */
export const SEVERITY_ORDER = [
    'info',
    'low',
    'medium',
    'high',
    'critical',
];
const SEVERITY_RANK = {
    info: 0,
    low: 1,
    medium: 2,
    high: 3,
    critical: 4,
};
/** True when `severity` is `minimum` or worse, which is how every filter reads. */
export const severityAtLeast = (severity, minimum) => SEVERITY_RANK[severity] >= SEVERITY_RANK[minimum];
/** The severities at or above `minimum`, for a SQL `IN (…)` or a filter chip row. */
export const severitiesAtLeast = (minimum) => SEVERITY_ORDER.filter((each) => severityAtLeast(each, minimum));
//# sourceMappingURL=basics.js.map